import { Button, Modal } from '@perry-weather/component-library';
import { Grid, useTheme, Typography, Stack, CircularProgress } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

interface ConfirmationDialogProps {
  confirmationQuestion: string;
  confirmationInfo: string;
  completionTitle: string;
  completionInfo: string;
  button: JSX.Element;
  acceptButton?: JSX.Element;
  rejectButton?: JSX.Element;
  acceptButtonText?: string;
  rejectButtonText?: string;
  disabled?: boolean;
  children?: JSX.Element;
  onConfirm?: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  onCompletion?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: () => void;
  onError?: (error: any) => void;
}

export function ConfirmationDialog(props: ConfirmationDialogProps) {
  const theme = useTheme();
  const {
    confirmationQuestion,
    confirmationInfo,
    completionTitle,
    completionInfo,
    button,
    disabled,
    acceptButton,
    rejectButton,
    acceptButtonText,
    rejectButtonText,
    children,
    onConfirm,
    onCompletion,
    onCancel,
    onError,
  } = props;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onConfirm) return;

    setIsLoading(true);
    onConfirm(e)
      .then(() => setIsCompleted(true))
      .catch(error => onError?.(error))
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setDialogOpen(false);
  };

  const handleConfirmCompletion = (e: React.MouseEvent<HTMLButtonElement>) => {
    onCompletion?.(e);
    setDialogOpen(false);
  };

  const ConfirmationModal = useMemo(() => {
    return (props: { children: React.ReactNode }) => (
      <Modal
        disabled={disabled}
        modalState={{ open: dialogOpen, handleOpen: () => setDialogOpen(true), handleClose: () => {} }}
        modalButton={button}
        sx={{ paper: { backgroundColor: theme.palette.background.default } }}>
        {props.children}
      </Modal>
    );
  }, [disabled, dialogOpen, button]);

  return (
    <ConfirmationModal>
      <Stack>
        <Typography variant='h6' fontWeight='bold'>
          {isCompleted ? completionTitle : confirmationQuestion}
        </Typography>

        <Grid item>
          {!children && <Typography variant='body1'>{isCompleted ? completionInfo : confirmationInfo}</Typography>}
          {children}
        </Grid>

        <Stack direction='row' marginTop={4} gap={1} alignSelf='end'>
          {isCompleted ? (
            <Button type='primary' onClick={handleConfirmCompletion}>
              <Typography variant='body1'>Got it!</Typography>
            </Button>
          ) : (
            <>
              {rejectButton ? (
                rejectButton
              ) : (
                <Button type='clear' disabled={isLoading} onClick={handleCancel}>
                  <Typography variant='body1'>{rejectButtonText ? rejectButtonText : 'Cancel'}</Typography>
                </Button>
              )}

              {acceptButton ? (
                acceptButton
              ) : (
                <Button type='primary' disabled={isLoading} onClick={handleConfirm}>
                  {isLoading ? (
                    <CircularProgress color='inherit' size='26px' />
                  ) : (
                    <Typography variant='body1'>{acceptButtonText ? acceptButtonText : 'Confirm'}</Typography>
                  )}
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </ConfirmationModal>
  );
}
